/**
 * Grid bookcase intake (plan §4.3): the classifier paragraph, the parameter block (Zod, sent
 * to Claude as part of the structured-output schema) and post-fit processing (ratios to
 * millimetres against the published or starting size, tick snapping).
 */
import { z } from 'zod';
import type { Design } from '../../domain/schemas';
import { CONSTRUCTION, type ConstructionProfile } from '../../domain/profiles';
import { mm, sum, toMm } from '../../domain/units';
import type { FamilyIntake } from '../types';
import { isBoardStock, type AnyStock } from '../stock';
import { FOOT, footKindFor } from '../base';
import { DEFAULT_APPEARANCE, evenOpenings, FAMILY, thicknessOf } from './design';
import { baseFromFit, BaseFitShape, FrontsFitShape, FRONTS_GUIDE, SideFitShape, sidesFromFit } from '../fit-common';

export const GRID_CLASSIFIER = `**grid-bookcase** — a closed rectangular case: two full-height side panels that reach the floor (or a plinth between them), a top and a bottom fitted between the sides, and one to four bays side by side separated by full-height vertical dividers that run unbroken from the bottom to the top. Each bay has its own fixed horizontal shelves, which stop at the verticals either side; shelf heights may differ from bay to bay. Back: a panel behind the shelves (overlay or set into a rebate) or none. The outline is a plain rectangle. Includes the classic single-bay bookcase, wide low bookcases, two- and three-bay units and units on a recessed plinth.
Rules it out: verticals that stop at a shelf and change position from row to row (offset or brick-bond grids); a lattice of equal square cubes; shelves that overhang or step out past the sides (staggered or cantilever shelves); legs or posts instead of side panels; ladders or leaning frames; a piece that is all drawers and no case; glazed or stacked barrister sections; metal frames. Doors, glazed doors, sliding doors and drawers over some or all of the openings do not rule it out: they are fitted as fronts.`;

export const GridFitParamsSchema = z.object({
  bays: z.int().min(0).describe('Number of bays side by side, separated by full-height dividers (1 for a single bookcase).'),
  bayWidthRatios: z.array(z.number().min(0).max(1)).describe('Each bay\'s clear width divided by the total clear width of all bays, left to right; one per bay; they sum to 1.'),
  bayLayouts: z.array(z.object({
    shelfCount: z.int().min(0).describe('Fixed internal shelves in this bay, not counting the top and bottom.'),
    spacing: z.enum(['even', 'uneven', 'unknown']).describe('even when this bay\'s openings look equal (within about 3 % of the clear height), uneven when they visibly differ.'),
    openingRatios: z.array(z.number().min(0).max(1)).describe('Every opening of this bay bottom-up (first = lowest): its clear height divided by the bay\'s total clear height; shelfCount + 1 of them, summing to 1.'),
  })).describe('One entry per bay, left to right.'),
  back: z.enum(['overlay', 'rebated', 'none', 'unknown']).describe('overlay: a back panel whose edge shows at the sides; rebated: a back set inside the case; none: open back (wall visible).'),
  ...BaseFitShape,
  ...FrontsFitShape,
  ...SideFitShape,
});
/** The fitted block (input form: fields with defaults may be absent in older recordings, which used `plinth: 'yes' | 'no'`). */
export type GridFitParams = z.input<typeof GridFitParamsSchema> & { plinth?: 'yes' | 'no' | 'unknown'; plinthOverHeight?: number };

export const GRID_PARAMS_GUIDE = `For grid-bookcase fill params["grid-bookcase"]: count the bays from the full-height verticals (uprights − 1); give each bay's clear width as a fraction of the total clear width; for each bay, left to right, count its fixed shelves and give every opening's clear height as a fraction of that bay's clear height, bottom-up, summing to 1 (put any rounding on the lowest opening); say whether there is a back, what the bottom stands on (a plinth under the bottom, separate feet or legs, or the floor) and whether the sides are solid or slatted. Ratios come from the clearest frontal photo, corrected for perspective. ${FRONTS_GUIDE}`;

export const GRID_INTAKE: FamilyIntake<GridFitParams> = {
  classifier: GRID_CLASSIFIER,
  paramsSchema: GridFitParamsSchema,
  paramsGuide: GRID_PARAMS_GUIDE,
};

/** Internal consistency of a fitted block; a message makes the analysis fail as malformed. */
export function checkGridParams(p: GridFitParams): string | null {
  if (p.bayWidthRatios.length !== p.bays) return `${p.bayWidthRatios.length} bay width ratios for ${p.bays} bays`;
  if (p.bayLayouts.length !== p.bays) return `${p.bayLayouts.length} bay layouts for ${p.bays} bays`;
  const w = sum(p.bayWidthRatios);
  if (Math.abs(w - 1) > 0.03) return `bay width ratios sum to ${w.toFixed(3)}`;
  if (p.bays < 1) return 'no bays';
  for (const [i, l] of p.bayLayouts.entries()) {
    if (l.openingRatios.length !== l.shelfCount + 1) return `bay ${i + 1}: ${l.openingRatios.length} openings for ${l.shelfCount} shelves`;
    const total = sum(l.openingRatios);
    if (Math.abs(total - 1) > 0.02) return `bay ${i + 1}: opening ratios sum to ${total.toFixed(3)}`;
  }
  return null;
}

/** Split `total` ticks by `ratios` (first-slice rule: each rounded to whole millimetres, residual on the first). */
function splitByRatios(total: number, ratios: number[]): number[] {
  const g = ratios.map((r) => mm(Math.round(toMm(total) * r)));
  if (g.length) g[0]! += total - sum(g);
  return g;
}

export interface GridDesignInput {
  W: number;
  H: number;
  D: number;
  /** The fitted block; null for a manual setup (one even bay). */
  params: GridFitParams | null;
  /** Bay 1's shelf count and Even when no block was fitted (first-slice proposals). */
  fallback?: { n: number; even: boolean; openingRatios: number[] | null };
  stock: AnyStock;
  construction?: ConstructionProfile;
  /** Clamp the bay count into the family's range. */
}

/** A design from fitted proportions and the (published or starting) size in ticks. */
export function gridDesignFromFit(input: GridDesignInput): Design {
  const { W, H, D, params, stock } = input;
  const construction = input.construction ?? CONSTRUCTION;
  const t = thicknessOf(stock);
  const maxBays = construction.bays?.max ?? 4;
  const bays = Math.max(1, Math.min(maxBays, params?.bays ?? 1));
  const fitBase = baseFromFit(params, H, input.D);
  const plinthTicks = fitBase.base === 'plinth' ? Math.min(construction.bays?.maxPlinthTicks ?? mm(150), fitBase.plinth!) : 0;
  // feet take their own height: turned 100 mm feet under solid timber, 20 mm levelling feet under ply
  const lift = fitBase.base === 'feet' ? fitBase.feetHeight ?? FOOT[footKindFor(isBoardStock(stock) ? 'board' : 'sheet')].default : fitBase.base === 'sides' ? fitBase.toeHeight! : plinthTicks;
  const layouts = params
    ? params.bayLayouts.slice(0, bays).map((l) => ({ n: l.shelfCount, even: l.spacing !== 'uneven', ratios: l.openingRatios }))
    : [{ n: input.fallback?.n ?? 3, even: input.fallback?.even ?? true, ratios: input.fallback?.openingRatios ?? [] }];
  while (layouts.length < bays) layouts.push({ ...layouts[0]! });
  const toLayout = (l: { n: number; even: boolean; ratios: number[] }) => {
    const A = H - lift - (l.n + 2) * t;
    const even = l.even || l.ratios.length !== l.n + 1;
    return { n: l.n, even, openings: even ? evenOpenings(A, l.n + 1) : splitByRatios(A, l.ratios) };
  };
  const first = toLayout(layouts[0]!);
  const d: Design = {
    family: FAMILY,
    W, H, D, n: first.n, even: first.even, openings: first.openings,
    stockProfileId: stock.id, constructionProfileId: construction.id, appearance: { ...DEFAULT_APPEARANCE },
  };
  if (plinthTicks) d.plinth = plinthTicks;
  if (fitBase.base === 'plinth' && fitBase.plinthSetback) { d.base = 'plinth'; d.plinthSetback = fitBase.plinthSetback; }
  if (fitBase.base === 'feet') { d.base = 'feet'; if (fitBase.feetHeight) d.feetHeight = fitBase.feetHeight; }
  if (fitBase.base === 'sides') { d.base = 'sides'; d.toeHeight = fitBase.toeHeight!; }
  const sides = sidesFromFit(params, input.D);
  if (sides.sideStyle === 'slatted') { d.sideStyle = 'slatted'; d.slatWidth = sides.slatWidth!; d.slatGap = sides.slatGap!; }
  if (params && params.back !== 'overlay' && params.back !== 'unknown') d.back = params.back;
  if (bays > 1) {
    d.bays = bays;
    const raw = params?.bayWidthRatios.length === bays ? params.bayWidthRatios : new Array<number>(bays).fill(1 / bays);
    const total = sum(raw) || 1;
    d.bayWidths = splitByRatios(W - (bays + 1) * t, raw.map((r) => r / total));
    d.bayLayouts = layouts.slice(1).map(toLayout);
  }
  return d;
}

/** "3 bays · 3 shelves each" / "1 bay · 4 shelves" — the family card's summary of a fit. */
export function gridFitSummary(p: GridFitParams): string {
  const counts = p.bayLayouts.map((l) => l.shelfCount);
  const same = counts.every((c) => c === counts[0]);
  const shelves = same ? `${counts[0]} shel${counts[0] === 1 ? 'f' : 'ves'}${p.bays > 1 ? ' each' : ''}` : `${counts.join(' / ')} shelves`;
  return `${p.bays} bay${p.bays === 1 ? '' : 's'} · ${shelves}`;
}
